import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import L from 'leaflet';
import { Globe2, Trash2, Eye, EyeOff, FileUp, Plus } from 'lucide-react';
import { uid } from '../lib/model';
import { EPSG_NAMES, fromGeoTiff, fromWorldFile } from '../lib/georef';
import CadPanel from './CadPanel';

const WKEY = 'giss_wms';
export const getWms = () => { try { return JSON.parse(localStorage.getItem(WKEY)) || []; } catch { return []; } };
const setWms = (l) => { localStorage.setItem(WKEY, JSON.stringify(l)); window.dispatchEvent(new Event('giss-wms')); };
const mpp = (c, w) => { const k = Math.PI / 180, a = c.tl, b = c.tr, x = (b[1] - a[1]) * k * Math.cos(((a[0] + b[0]) / 2) * k), y = (b[0] - a[0]) * k; return (Math.hypot(x, y) * 6371000) / w; };

// map: WMS services (cadastre, utility plans) as online layers
export function useWmsLayers(mapRef) {
  const [l, setL] = useState(getWms);
  useEffect(() => { const h = () => setL(getWms()); window.addEventListener('giss-wms', h); return () => window.removeEventListener('giss-wms', h); }, []);
  const key = l.filter((x) => x.on !== false).map((x) => `${x.url}|${x.layers}|${x.op}`).join(';');
  useEffect(() => {
    const map = mapRef.current; if (!map || !key) return undefined;
    const ls = l.filter((x) => x.on !== false).map((x) => L.tileLayer.wms(x.url, { layers: x.layers, format: 'image/png', transparent: true, version: '1.3.0', opacity: x.op ?? 0.8, maxZoom: 22, zIndex: 350 }).addTo(map));
    return () => ls.forEach((y) => y.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, key]);
}

function Wms({ t }) {
  const [l, setL] = useState(getWms);
  const [f, setF] = useState({ name: '', url: '', layers: '' });
  const [caps, setCaps] = useState([]);
  const save = (n) => { setWms(n); setL(n); };
  const loadCaps = async () => {
    try {
      const u = new URL(f.url); u.searchParams.set('service', 'WMS'); u.searchParams.set('request', 'GetCapabilities');
      const x = new DOMParser().parseFromString(await (await fetch(u)).text(), 'text/xml');
      const ls = [...x.querySelectorAll('Layer > Name')].map((n) => ({ name: n.textContent, title: n.parentElement.querySelector(':scope > Title')?.textContent || n.textContent }));
      setCaps(ls); if (!ls.length) toast.error(t.mdCapsNone);
    } catch { toast.error(t.mdCapsFail); }
  };
  return (
    <div className="irr-zone" data-testid="wms-section">
      <b><Globe2 size={15} /> {t.mdWms}</b>
      <p className="muted small-hint">{t.mdWmsHint}</p>
      <div className="row">
        <input className="offer-in" data-testid="wms-name" placeholder={t.mdWmsName} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <input className="offer-in" data-testid="wms-url" placeholder="https://…/wms" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value.trim() })} />
        <button type="button" className="btn btn-outline btn-sm" data-testid="wms-caps-btn" disabled={!/^https:\/\//.test(f.url)} onClick={loadCaps}>{t.mdCaps}</button>
      </div>
      <div className="row">
        {caps.length ? <select data-testid="wms-layer-select" value={f.layers} onChange={(e) => setF({ ...f, layers: e.target.value })}><option value="">–</option>{caps.map((c) => <option key={c.name} value={c.name}>{c.title}</option>)}</select>
          : <input className="offer-in" data-testid="wms-layers" placeholder={t.mdWmsLayers} value={f.layers} onChange={(e) => setF({ ...f, layers: e.target.value.trim() })} />}
        <button type="button" className="btn btn-green btn-sm" data-testid="wms-add-btn" disabled={!/^https:\/\//.test(f.url) || !f.layers} onClick={() => { save([...l, { id: uid('wms'), name: f.name || f.layers, url: f.url, layers: f.layers, op: 0.8, on: true }]); setF({ name: '', url: '', layers: '' }); setCaps([]); toast.success(t.mdWmsAdded); }}><Plus size={14} /> {t.mdAdd}</button>
      </div>
      {l.map((x) => (
        <div key={x.id} className="row" data-testid={`wms-item-${x.id}`}>
          <span className="wms-name">{x.name}</span>
          <input type="range" min="0.2" max="1" step="0.05" data-testid={`wms-opacity-${x.id}`} value={x.op ?? 0.8} onChange={(e) => save(l.map((y) => (y.id === x.id ? { ...y, op: +e.target.value } : y)))} />
          <button className="icon-btn" data-testid={`wms-toggle-${x.id}`} onClick={() => save(l.map((y) => (y.id === x.id ? { ...y, on: y.on === false } : y)))}>{x.on === false ? <EyeOff size={15} /> : <Eye size={15} />}</button>
          <button className="icon-btn danger" data-testid={`wms-del-${x.id}`} onClick={() => save(l.filter((y) => y.id !== x.id))}><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );
}

function GeoRef({ t, onAdd }) {
  const tif = useRef(null), im = useRef(null), wf = useRef(null);
  const [img, setImg] = useState(null), [wld, setWld] = useState(null), [epsg, setEpsg] = useState('31255'), [busy, setBusy] = useState(false);
  const run = async (fn, name) => { setBusy(true); try { const r = await fn(); onAdd({ id: uid('site'), name: name.replace(/\.[^.]+$/, ''), src: r.src, w: r.w, h: r.h, op: 0.8, on: true, c: { ...r.c, mpp: mpp(r.c, r.w) } }); toast.success(t.spAlignedOk); } catch { toast.error(t.mdGeoFail); } setBusy(false); };
  return (
    <div className="irr-zone" data-testid="georef-section">
      <b><FileUp size={15} /> {t.mdGeo}</b>
      <p className="muted small-hint">{t.mdGeoHint}</p>
      <div className="row">
        <label className="pit-num"><span>{t.mdCrs}</span><select data-testid="georef-epsg" value={epsg} onChange={(e) => setEpsg(e.target.value)}>{Object.entries(EPSG_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
      </div>
      <div className="row">
        <button type="button" className="btn btn-outline btn-sm" data-testid="georef-tif-btn" disabled={busy} onClick={() => tif.current.click()}>GeoTIFF</button>
        <input ref={tif} type="file" hidden accept=".tif,.tiff,image/tiff" data-testid="georef-tif-input" onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) run(() => fromGeoTiff(f), f.name); }} />
        <button type="button" className="btn btn-outline btn-sm" data-testid="georef-img-btn" onClick={() => im.current.click()}>{img ? img.name.slice(0, 18) : t.mdImg}</button>
        <input ref={im} type="file" hidden accept="image/*" data-testid="georef-img-input" onChange={(e) => setImg(e.target.files[0] || null)} />
        <button type="button" className="btn btn-outline btn-sm" data-testid="georef-wld-btn" onClick={() => wf.current.click()}>{wld ? wld.name.slice(0, 18) : t.mdWld}</button>
        <input ref={wf} type="file" hidden accept=".jgw,.pgw,.tfw,.wld,.j2w,.gfw,.txt" data-testid="georef-wld-input" onChange={(e) => setWld(e.target.files[0] || null)} />
        <button type="button" className="btn btn-green btn-sm" data-testid="georef-apply-btn" disabled={!img || !wld || busy} onClick={async () => run(async () => fromWorldFile(img, await wld.text(), +epsg), img.name)}>{t.mdPlace}</button>
      </div>
    </div>
  );
}

// photo branch: all map documents in one place (CAD, georeferenced images, WMS); plans without reference are aligned above
export default function MapDocsExtra({ t, project, updateProject, getCenter }) {
  return (
    <>
      <GeoRef t={t} onAdd={(s) => updateProject((pr) => ({ ...pr, sites: [...(pr.sites || []), s] }))} />
      <Wms t={t} />
      <div className="irr-zone" data-testid="cad-section"><CadPanel t={t} project={project} updateProject={updateProject} getCenter={getCenter} /></div>
    </>
  );
}
